import { z } from 'zod';

/** Building blocks reused across every entity schema. */

export const uuid = z.uuid();
export const uuidNullable = z.uuid().nullable();

/** ISO-639-1 language code. Language is stored as language; geo is a separate column (§0.3). */
export const languageCode = z
  .string()
  .min(2)
  .max(3)
  .regex(/^[a-z]{2,3}$/u, 'ISO-639-1 lowercase code');

/** ISO-3166-1 alpha-2. */
export const countryCode = z
  .string()
  .length(2)
  .regex(/^[A-Z]{2}$/u, 'ISO-3166-1 alpha-2 uppercase');

/** Hostnames may be IDN; we store the unicode form and punycode on render. */
export const domainName = z
  .string()
  .min(4)
  .max(253)
  .regex(
    /^(?!-)[\p{L}\p{N}-]{1,63}(?<!-)(\.(?!-)[\p{L}\p{N}-]{1,63}(?<!-))+$/u,
    'not a valid domain name',
  );

export const hexColor = z
  .string()
  .regex(/^#[0-9a-fA-F]{6}$/u, 'expected #rrggbb');

export const urlHttp = z
  .url()
  .refine((v) => v.startsWith('http://') || v.startsWith('https://'), 'only http/https');

export const slug = z
  .string()
  .min(1)
  .max(120)
  .regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/u, 'lowercase kebab-case');

export const urlPath = z
  .string()
  .min(1)
  .max(512)
  .regex(/^\/[\w\-./]*$/u, 'must start with / and contain no query or fragment');

/** Optimistic locking token (§4.6). Every editable entity carries it. */
export const version = z.number().int().nonnegative();

export const timestamps = z.object({
  createdAt: z.date(),
  updatedAt: z.date(),
});

export const idInput = z.object({ id: uuid });

export const sortDirection = z.enum(['asc', 'desc']);

/** A `(geo, language)` pair — the mandatory addressing unit for keywords and content. */
export const geoLangSchema = z.object({
  geoId: uuid,
  language: languageCode,
});
export type GeoLang = z.infer<typeof geoLangSchema>;

/** hreflang tag value: `{lang}-{COUNTRY}`, built on the fly, never stored (§5). */
export function hreflangTag(language: string, country: string): string {
  return `${language.toLowerCase()}-${country.toUpperCase()}`;
}

/** Masked secret shown in API responses: never the value, always shape + last4. */
export const maskedSecret = z.object({
  masked: z.literal('••••'),
  last4: z.string().length(4).nullable(),
  present: z.boolean(),
});
export type MaskedSecret = z.infer<typeof maskedSecret>;

export function maskSecret(value: string | null | undefined): MaskedSecret {
  if (!value) return { masked: '••••', last4: null, present: false };
  return { masked: '••••', last4: value.slice(-4).padStart(4, '•'), present: true };
}

/** Percentage 0..100 with one decimal — used by every `*_percent` derived field. */
export const percent = z.number().min(0).max(100);

export const auditRef = z.object({
  authorId: uuidNullable,
  authorName: z.string().nullable(),
  at: z.date(),
});
